'use client';

import React, { useState, useCallback, memo, useRef, useEffect } from 'react';
import { useBloodVisionIndia } from 'components/blood-vision/BloodVisionIndiaContext';
import styles from './InputPincode.module.css';

// Validation function for Indian pincodes (6 digits)
const isValidIndianPincode = (pincode: string): boolean => {
  // Indian pincode must be exactly 6 digits
  const pincodeRegex = /^[1-9][0-9]{5}$/;
  return pincodeRegex.test(pincode);
};

// Component Props
interface InputPincodeProps {
  onPincodeChange?: (pincode: string, isValid: boolean) => void;
  className?: string;
}

// Persistent pincode storage key
const PINCODE_STORAGE_KEY = 'blood_vision_india_pincode';

// Memoized component to prevent re-renders from context updates
export const InputPincode: React.FC<InputPincodeProps> = memo(
  ({ onPincodeChange, className }) => {
    // Only extract fetchPlansIndia to avoid re-renders from context updates
    const { fetchPlansIndia, pincodeInfo } = useBloodVisionIndia();

    // Use ref to store pincode persistently across re-renders
    const pincodeRef = useRef<string>('');

    // Input ref for focusing
    const inputRef = useRef<HTMLInputElement>(null);

    // Initialize state from ref or sessionStorage
    const [pincode, setPincodeInternal] = useState<string>(() => {
      // Try to get from sessionStorage first
      if (typeof window !== 'undefined') {
        const stored = sessionStorage.getItem(PINCODE_STORAGE_KEY);
        if (stored) {
          pincodeRef.current = stored;
          return stored;
        }
      }
      return '';
    });

    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean>(false);
    // Track last auto-submitted pincode to prevent duplicate submissions
    const lastAutoSubmittedRef = useRef<string>('');

    // Custom setPincode that also updates ref and storage
    const setPincode = useCallback((value: string) => {
      pincodeRef.current = value;
      setPincodeInternal(value);

      // Persist to sessionStorage
      if (typeof window !== 'undefined') {
        if (value) {
          sessionStorage.setItem(PINCODE_STORAGE_KEY, value);
        } else {
          sessionStorage.removeItem(PINCODE_STORAGE_KEY);
        }
      }
    }, []);

    // Don't pre-fill pincode on first load - keep input blank
    // Once user enters pincode, it stays in the input until they edit it

    // Restore pincode on mount if it was lost
    useEffect(() => {
      if (!pincode && pincodeRef.current) {
        setPincodeInternal(pincodeRef.current);
      }
    }, [pincode]);

    // Clear input when pincodeInfo is reset (coming back from other pages)
    useEffect(() => {
      if (!pincodeInfo && (pincode || pincodeRef.current)) {
        console.log('📍 Clearing pincode input - context reset');
        setPincode(''); // This will also clear sessionStorage
        setIsValid(false);
        setError('');
      }
    }, [pincodeInfo, pincode, setPincode]);

    // Debug: Log when component renders (can be removed in production)
    // Removed to improve performance - uncomment only for debugging
    // useEffect(() => {
    //   console.log(
    //     '📍 InputPincode rendered with value:',
    //     pincodeRef.current || pincode || '[empty]',
    //   );
    // });

    const validateAndFetchPackages = useCallback(
      async (pincodeValue: string) => {
        // Reset states
        setError('');
        setIsValid(false);

        // Basic format validation
        if (!isValidIndianPincode(pincodeValue)) {
          setError('Please enter a valid 6-digit pincode');
          setIsValidating(false);
          if (onPincodeChange) {
            onPincodeChange(pincodeValue, false);
          }
          return;
        }

        // Blur input to dismiss keyboard on mobile devices before API call
        inputRef.current?.blur();

        setIsValidating(true);

        try {
          // Fetch packages with the pincode (user-provided)
          // This will enable the plan card "Add" buttons
          await fetchPlansIndia(pincodeValue, true);

          // Mark as valid after successful fetch
          setIsValid(true);
          setIsValidating(false);

          if (onPincodeChange) {
            onPincodeChange(pincodeValue, true);
          }
        } catch (err) {
          console.error('Error validating pincode:', err);
          setError('Failed to validate pincode. Please try again.');
          setIsValidating(false);
          if (onPincodeChange) {
            onPincodeChange(pincodeValue, false);
          }
        }
      },
      [fetchPlansIndia, onPincodeChange],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Only allow numbers and limit to 6 digits
        if (value && !/^\d*$/.test(value)) {
          return;
        }

        if (value.length > 6) {
          return;
        }

        // Reset validation state for incomplete input
        setError('');
        setIsValid(false);

        // Update pincode (this will trigger auto-submit effect if criteria met)
        setPincode(value);

        // Notify parent of change
        if (onPincodeChange) {
          onPincodeChange(value, false);
        }
      },
      [onPincodeChange, setPincode],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow: backspace, delete, tab, escape, enter
        if ([8, 9, 27, 13, 46].includes(e.keyCode)) {
          return;
        }

        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
          return;
        }

        // Prevent if not a number
        if (
          (e.keyCode < 48 || e.keyCode > 57) &&
          (e.keyCode < 96 || e.keyCode > 105)
        ) {
          e.preventDefault();
        }
      },
      [],
    );

    // Reset auto-submit tracking when pincode length changes from 6 to less than 6
    // This allows re-submission of the same pincode after deletion
    useEffect(() => {
      if (pincode.length < 6 && lastAutoSubmittedRef.current) {
        lastAutoSubmittedRef.current = '';
      }
    }, [pincode.length]);

    // Auto-submit when criteria is met (6 digits and valid format)
    // Memoized to prevent unnecessary re-runs
    useEffect(() => {
      // Early returns to avoid unnecessary checks
      if (pincode.length !== 6) return;
      if (!isValidIndianPincode(pincode)) return;
      if (isValidating) return;
      if (isValid) return;
      if (error) return;
      if (lastAutoSubmittedRef.current === pincode) return;

      // All criteria met - auto-submit
      lastAutoSubmittedRef.current = pincode;
      validateAndFetchPackages(pincode);
    }, [pincode, isValidating, isValid, error]);
    // Note: validateAndFetchPackages is intentionally excluded from deps
    // as it's stable (useCallback with stable deps) and including it causes unnecessary re-runs

    // Handle submit button click (kept for backward compatibility, but won't be shown)
    // const handleSubmit = useCallback(() => {
    //   if (pincode.length === 6) {
    //     validateAndFetchPackages(pincode);
    //   }
    // }, [pincode, validateAndFetchPackages]);

    // Handle edit button click
    const handleEdit = useCallback(() => {
      // Focus the input field
      inputRef.current?.focus();
      // Optionally clear the input or keep it for editing
      // For now, we'll keep the value so user can edit it
    }, []);

    return (
      <div className={className} style={{ width: '100%', maxWidth: '100%' }}>
        <div className={styles.container}>
          <div className={styles.innerContainer}>
            <div className={styles.labelContainer}>
              <p className={styles.label}>See Plans in Your Area</p>
              <p className={styles.subLabel}>
                Enter pincode to check plan availability.
              </p>
            </div>

            <div
              className={styles.inputWrapper}
              data-error={!!error}
              data-valid={isValid}
            >
              <input
                className={styles.input}
                ref={inputRef}
                id="blood-vision-pincode-input"
                type="text"
                inputMode="numeric"
                placeholder="Enter Pin Code"
                value={pincode}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                maxLength={6}
                aria-label="Enter your 6-digit pincode"
                aria-invalid={!!error}
                aria-describedby={
                  error
                    ? 'pincode-error'
                    : isValid
                    ? 'pincode-success'
                    : undefined
                }
                autoFocus={true}
              />
              {/* Show loading spinner when validating */}
              {isValidating && <div className={styles.loadingSpinner} />}

              {/* Show Edit button when pincode is validated */}
              {isValid && !isValidating && (
                <button
                  className={`${styles.actionButton} ${styles.editButton}`}
                  onClick={handleEdit}
                  type="button"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {/* Hint message - shows when pincode is not 6 digits */}
          {pincode.length < 6 && !error && !isValidating && !isValid && (
            <div className={styles.messageContainer}>
              <p className={styles.hintMessage}>
                Please enter a 6 digit valid pincode
              </p>
            </div>
          )}

          {error && !isValidating && (
            <div className={styles.messageContainer}>
              <p className={styles.errorMessage} id="pincode-error" role="alert">
                {error}
              </p>
            </div>
          )}

          {/* Show warning message when pincode is not serviceable */}
          {isValid &&
            !isValidating &&
            pincodeInfo?.pincode_serviceable === false && (
              <div className={styles.messageContainer}>
                <p
                  className={styles.warningMessage}
                  id="pincode-warning"
                  role="alert"
                >
                  Your pincode is not serviceable currently
                </p>
              </div>
            )}

          {/* Show success message when pincode is serviceable */}
          {isValid &&
          !isValidating &&
          pincodeInfo?.pincode_serviceable === true &&
          pincodeInfo?.pincode_info?.city ? (
            <div
              className={styles.messageContainer}
              style={{
                justifyContent: 'flex-start',
                marginTop: '10px',
              }}
            >
              <p
                className={styles.successMessage}
                id="pincode-success"
                role="status"
              >
                Showing plans for {pincodeInfo?.pincode_info?.city},{' '}
                {pincodeInfo?.pincode_info?.state}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for memo
    // Only re-render if className or onPincodeChange reference changes
    // This prevents re-renders when context updates
    return (
      prevProps.className === nextProps.className &&
      prevProps.onPincodeChange === nextProps.onPincodeChange
    );
  },
);

InputPincode.displayName = 'InputPincode';

export default InputPincode;
